"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { appendReturnTo } from "@/core/auth/return-to";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { getAuthFlashError, startAuthIntent, toAbsoluteApiUrl } from "@/core/auth/auth-api";
import { type AuthEntryPoint, type AuthFlashError } from "@/core/auth/types";
import { useAsyncAction } from "@/shared/hooks/use-async-action";
import { resolveOrNull } from "@/shared/lib/async-result";
import type { AuthEntryCardContent } from "@/features/auth/types/auth-entry-page-content";
import Env from "@/core/config/env";
interface AuthEntryCardProps {
  content: AuthEntryCardContent;
  entryPoint: AuthEntryPoint;
  returnTo?: string;
}
const ENTRY_THEME: Record<AuthEntryPoint, {
  accentClassName: string;
  bulletClassName: string;
}> = {
  SEEKER_SIGNUP: {
    accentClassName: "from-[#1b7c84] via-[#1f9aa0] to-[#68c9b8]",
    bulletClassName: "text-[#1f9aa0]"
  },
  SEEKER_LOGIN: {
    accentClassName: "from-[#1b7c84] via-[#1f9aa0] to-[#68c9b8]",
    bulletClassName: "text-[#1f9aa0]"
  },
  HEALER_SIGNUP: {
    accentClassName: "from-[#23526b] via-[#476a84] to-[#d8a96b]",
    bulletClassName: "text-[#476a84]"
  },
  HEALER_LOGIN: {
    accentClassName: "from-[#23526b] via-[#476a84] to-[#d8a96b]",
    bulletClassName: "text-[#476a84]"
  },
  ADMIN_LOGIN: {
    accentClassName: "from-[#1f2937] via-[#334155] to-[#64748b]",
    bulletClassName: "text-[#334155]"
  }
};
export function AuthEntryCard(t0) {
  const $ = _c(55);
  const {
    content,
    entryPoint,
    returnTo
  } = t0;
  const continueAction = useAsyncAction();
  const [flashError, setFlashError] = useState(null);
  const theme = ENTRY_THEME[entryPoint];
  const visibleError = continueAction.state.error?.message ?? flashError?.message;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let isActive = true;
      resolveOrNull(async () => {
        const nextFlashError = await getAuthFlashError();
        if (isActive) {
          setFlashError(nextFlashError);
        }
        return nextFlashError;
      });
      return () => {
        isActive = false;
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== continueAction || $[3] !== entryPoint || $[4] !== returnTo) {
    t3 = async function handleContinue() {
      setFlashError(null);
      await continueAction.run(async () => {
        const response = await startAuthIntent(entryPoint, returnTo);
        const loginTarget = response.loginUrl ? toAbsoluteApiUrl(response.loginUrl) : Env.apiEndpoint("/auth/login");
        window.location.assign(loginTarget);
      });
    };
    $[2] = continueAction;
    $[3] = entryPoint;
    $[4] = returnTo;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleContinue = t3;
  const t4 = `h-2 w-full bg-gradient-to-r ${theme.accentClassName}`;
  let t5;
  if ($[6] !== t4) {
    t5 = <div className={t4} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== content.badge) {
    t6 = <span className="inline-flex w-fit rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold tracking-[0.26em] text-white uppercase">{content.badge}</span>;
    $[8] = content.badge;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== content.title) {
    t7 = <CardTitle className="font-heading text-3xl font-normal tracking-tight text-slate-950">{content.title}</CardTitle>;
    $[10] = content.title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== content.description) {
    t8 = <CardDescription className="max-w-xl text-base leading-7 text-slate-600">{content.description}</CardDescription>;
    $[12] = content.description;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t7 || $[16] !== t8) {
    t9 = <CardHeader className="gap-4 px-8 pt-8">{t6}{t7}{t8}</CardHeader>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== visibleError) {
    t10 = visibleError ? <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{visibleError}</div> : null;
    $[18] = visibleError;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== content.checklist || $[21] !== theme.bulletClassName) {
    let t12;
    if ($[23] !== theme.bulletClassName) {
      t12 = item => <div key={item} className="flex items-start gap-3 text-sm leading-6 text-slate-700"><CheckCircle2 className={`mt-0.5 size-4 shrink-0 ${theme.bulletClassName}`} /><span>{item}</span></div>;
      $[23] = theme.bulletClassName;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    t11 = content.checklist.map(t12);
    $[20] = content.checklist;
    $[21] = theme.bulletClassName;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <div className="mb-6 space-y-3 rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4">{t11}</div>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = continueAction.state.loading ? content.loadingLabel : content.buttonLabel;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowRight className="size-4" />;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== continueAction.state.loading || $[29] !== handleContinue || $[30] !== t13) {
    t15 = <Button className="h-12 w-full rounded-full text-base" disabled={continueAction.state.loading} onClick={handleContinue}>{t13}{t14}</Button>;
    $[28] = continueAction.state.loading;
    $[29] = handleContinue;
    $[30] = t13;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== content.secureNote) {
    t16 = <p className="mt-4 text-sm leading-6 text-slate-500">{content.secureNote}</p>;
    $[32] = content.secureNote;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== content.alternatePrompt) {
    t17 = <span className="text-white/72">{content.alternatePrompt}</span>;
    $[34] = content.alternatePrompt;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== content.alternateCtaHref || $[37] !== returnTo) {
    t18 = appendReturnTo(content.alternateCtaHref, returnTo);
    $[36] = content.alternateCtaHref;
    $[37] = returnTo;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== content.alternateCtaLabel || $[40] !== t18) {
    t19 = <Link className="font-semibold text-[#7de2f8] transition-colors hover:text-white" href={t18}>{content.alternateCtaLabel}</Link>;
    $[39] = content.alternateCtaLabel;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t17 || $[43] !== t19) {
    t20 = <div className="mt-6 flex flex-col gap-2 rounded-[1.3rem] bg-slate-950 px-4 py-4 text-sm text-white sm:flex-row sm:items-center sm:justify-between">{t17}{t19}</div>;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t10 || $[46] !== t12 || $[47] !== t15 || $[48] !== t16 || $[49] !== t20) {
    t21 = <CardContent className="px-8 pb-8">{t10}{t12}{t15}{t16}{t20}</CardContent>;
    $[45] = t10;
    $[46] = t12;
    $[47] = t15;
    $[48] = t16;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t21 || $[52] !== t5 || $[53] !== t9) {
    t22 = <Card className="overflow-hidden rounded-[2rem] border-white/50 bg-white/90 shadow-[0_32px_90px_rgba(15,23,42,0.14)] backdrop-blur">{t5}{t9}{t21}</Card>;
    $[51] = t21;
    $[52] = t5;
    $[53] = t9;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}